import { useEffect } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';

import { PrevHeaderBarWithTitleTemplate } from '#templates';

import { CurriculumType } from 'src/refactor_components/lecture-edit/curriculum-type';
import { CurriculumList } from 'src/refactor_components/lecture-edit/curriculum-list';
import { CurriculumBasic } from 'src/refactor_components/lecture-edit/curriculum-basic';
import { CurriculumImage } from 'src/refactor_components/lecture-edit/curriculum-image';

import { useMutation } from '@tanstack/react-query';
import {
  modifyCoachLecture,
  postCoachLecture,
  useGetLecture
} from '#api/lectures';
import { useToast } from '#providers/ToastProvider';
import { useNavigate, useParams } from 'react-router';
import { Images } from '#types/images';
import CurriculumNonRegular from 'src/refactor_components/lecture-edit/curriculum-non-regular';
import CurriculumPrice from 'src/refactor_components/lecture-edit/curriculum-price';
import { Curricula } from '#types/lectures';

const ClassesEditForm = () => {
  const { id } = useParams();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const methods = useForm<{
    curriculumType: string;
    curriculums: Curricula[];
    images: Images[];
  }>({
    defaultValues: {
      curriculumType: '정규',
      curriculums: [],
      images: [] as Images[]
    }
  });

  const curriculumTypeWathcer = useWatch({
    control: methods.control,
    name: 'curriculumType'
  });

  const { mutate: postLectureMutate } = useMutation(postCoachLecture, {
    onSuccess: () => {
      showToast({
        message: `${methods.getValues(
          'curriculumType'
        )} 수업 등록에 성공하였습니다.`
      });
      return navigate('/profile');
    },
    onError: () => {
      showToast({
        type: 'fail',
        message: `${methods.getValues(
          'curriculumType'
        )} 수업 등록에 실패하였습니다.`
      });
    }
  });

  const { mutate: modifyLectureMutate } = useMutation(modifyCoachLecture, {
    onSuccess: () => {
      showToast({
        message: `${methods.getValues('curriculumType')} 수업을 수정했습니다.`
      });
    },
    onError: () => {
      showToast({
        type: 'fail',
        message: `${methods.getValues(
          'curriculumType'
        )} 수업 수정을 다시 시도해주세요.`
      });
    }
  });

  const onPostSubmit = (data: any) => {
    const { curriculumType, price, images, ...rest } = data;

    let isOnePoint = false;
    let isOt = false;

    const revenue = price
      ? Math.floor((price - price * 0.033 * 1.1) / 10) * 10
      : 0;

    if (curriculumType === '원포인트') {
      isOnePoint = true;
    } else if (curriculumType === '체험수업') {
      isOt = true;
    }

    const imageIds = images.map((value: { id: string }) => value.id);

    postLectureMutate({
      isOnePoint,
      isOt,
      revenue,
      price,
      images: imageIds,
      ...rest
    });
  };

  const onModifySubmit = (data: any) => {
    const { curriculumType, price, images, ...rest } = data;
    let isOnePoint = false;
    let isOt = false;

    const netRevenue = price
      ? Math.floor((price - price * 0.033 * 1.1) / 10) * 10
      : 0;

    if (curriculumType === '원포인트') {
      isOnePoint = true;
    } else if (curriculumType === '체험수업') {
      isOt = true;
    }

    const imageIds = images.map((value: { id: string }) => value.id);

    modifyLectureMutate({
      lectureId: id ? id : '',
      payload: {
        isOnePoint,
        isOt,
        netRevenue,
        price,
        images: imageIds,
        ...rest
      }
    });
  };

  const { data, isSuccess } = useGetLecture(id ? id : '');

  useEffect(() => {
    if (isSuccess) {
      const { curricula, ...rest } = data;
      methods.reset({
        ...rest,
        curriculums: curricula,
        curriculumType: rest.isOt
          ? '체험'
          : data.isOnePoint
          ? '원포인트'
          : '정규'
      });
    }
  }, [data, isSuccess]);

  return (
    <PrevHeaderBarWithTitleTemplate
      title="수업"
      defaultBackground
      rightContents={
        <RightButtons
          id={id}
          handleSubmit={methods.handleSubmit(
            id ? onModifySubmit : onPostSubmit
          )}
        />
      }
    >
      <FormProvider {...methods}>
        <form className="p-20 flex flex-col gap-40">
          <CurriculumType />
          <CurriculumBasic />
          {curriculumTypeWathcer === '정규' ? (
            <>
              <CurriculumPrice />
              <CurriculumList />
            </>
          ) : (
            <CurriculumNonRegular />
          )}
          <CurriculumImage />
        </form>
      </FormProvider>
    </PrevHeaderBarWithTitleTemplate>
  );
};

const RightButtons = ({
  id,
  handleSubmit
}: {
  id?: string;
  handleSubmit: any;
}) => {
  return id ? (
    <div className="flex justify-between items-center gap-25">
      <button onClick={handleSubmit}>수정</button>
    </div>
  ) : (
    <button onClick={handleSubmit}>등록</button>
  );
};

export default ClassesEditForm;
